import React, { useState } from "react";
import {
  MessageSquare,
  Smartphone,
  CheckCircle,
  ArrowRight,
  Copy,
  Bot,
  Zap,
  Shield,
} from "lucide-react";
import WhatsappIcon from "../../../assets/images/whatsapp.png";
import { Link } from "react-router-dom";
const WhatsAppSetup = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("pending");
  const [phoneNumber] = useState("+234 415 523 8886");
  const [joinCode] = useState("join chose-cover");

  const steps = [
    { id: 1, title: "Connect to sandbox", icon: Shield },
    { id: 2, title: "AI-Initiated message", icon: Bot },
    { id: 3, title: "User-Initiated conversation", icon: MessageSquare },
    { id: 4, title: "Setup Complete", icon: CheckCircle },
  ];

  const handleConnect = async () => {
    setIsConnecting(true);
    setConnectionStatus("connecting");

    // Simulate AI bot connection process
    setTimeout(() => {
      setConnectionStatus("connected");
      setCurrentStep(2);
      setIsConnecting(false);
    }, 2000);
  };

  const handleNextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert("Phone number copied succesfully");
  };

  const copyToClipboardText = (text: string) => {
    navigator.clipboard.writeText(text);
    alert("Code copied succesfully");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-emerald-100 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <img src={WhatsappIcon} alt="WhatsApp Icon" />
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                WhatsApp Integration
              </h1>
              <p className="text-gray-500">Setup powered by CPaaS AI Bot</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep >= step.id;
              const isCurrent = currentStep === step.id;

              return (
                <div key={step.id} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-500 ${
                        isActive
                          ? "bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-200"
                          : "bg-gray-100 text-gray-400"
                      } ${
                        isCurrent ? "scale-110 ring-4 ring-emerald-100" : ""
                      }`}
                    >
                      <Icon className="w-6 h-6" />
                    </div>
                    <span
                      className={`mt-2 text-sm font-medium ${
                        isActive ? "text-emerald-600" : "text-gray-400"
                      }`}
                    >
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`w-24 h-0.5 mx-4 transition-colors duration-500 ${
                        currentStep > step.id ? "bg-emerald-400" : "bg-gray-200"
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-1 gap-12 items-start">
          {/* Left Panel */}
          <div className="space-y-8">
            {currentStep === 1 && (
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-emerald-100">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-emerald-100 to-teal-100 rounded-xl flex items-center justify-center">
                    <Bot className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      AI-Powered Connection
                    </h2>
                    <p className="text-gray-500">
                      Let our CPaaS AI bot handle the setup
                    </p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
                    <div className="flex items-start space-x-3">
                      <Zap className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <h3 className="font-semibold text-emerald-900">
                          Automated Setup
                        </h3>
                        <p className="text-emerald-700 text-sm mt-1">
                          Our AI bot will automatically configure your WhatsApp
                          sandbox connection, handle authentication, and verify
                          the integration.
                        </p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleConnect}
                    disabled={isConnecting}
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold py-4 rounded-xl hover:from-emerald-600 hover:to-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isConnecting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>AI Bot Connecting...</span>
                      </>
                    ) : (
                      <>
                        <Bot className="w-5 h-5" />
                        <span>Start AI Setup</span>
                      </>
                    )}
                  </button>

                  {connectionStatus === "connected" && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                      <div className="flex items-center space-x-2 text-green-800">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">
                          Sandbox Connected Successfully!
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-emerald-100">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-100 to-emerald-100 rounded-xl flex items-center justify-center">
                    <Bot className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      AI Message Delivery
                    </h2>
                    <p className="text-gray-500">Testing automated messaging</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-emerald-50 rounded-xl border border-blue-200">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      AI Bot Status
                    </h3>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-sm text-gray-600">
                        Sending test message via WhatsApp...
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={handleNextStep}
                    className="w-full bg-gradient-to-r from-blue-500 to-emerald-600 text-white font-semibold py-4 rounded-xl hover:from-blue-600 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                  >
                    <span>Continue to User Setup</span>
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {currentStep >= 3 && (
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-emerald-100">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-100 to-emerald-100 rounded-xl flex items-center justify-center">
                    <Smartphone className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Complete Connection
                    </h2>
                    <p className="text-gray-500">
                      Connect your device to WhatsApp
                    </p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">
                      Send WhatsApp Message
                    </h3>
                    <div className="bg-gradient-to-r from-gray-50 to-emerald-50 p-4 rounded-xl border border-gray-200">
                      <p className="text-sm text-gray-600 mb-3">
                        Use WhatsApp and send a message from your device to:
                      </p>
                      <div className="flex items-center space-x-2 bg-white p-3 rounded-lg border">
                        <MessageSquare className="w-5 h-5 text-emerald-600" />
                        <span className="font-mono font-semibold text-emerald-800">
                          {phoneNumber}
                        </span>
                        <button
                          onClick={() => copyToClipboard(phoneNumber)}
                          className="p-1 hover:bg-gray-100 rounded"
                          title="Copy number"
                        >
                          <Copy className="w-4 h-4 text-gray-500" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">
                      With Code
                    </h3>
                    <div className="bg-gradient-to-r from-gray-50 to-emerald-50 p-4 rounded-xl border border-gray-200">
                      <div className="flex items-center space-x-2 bg-white p-3 rounded-lg border">
                        <span className="font-mono font-semibold text-gray-800">
                          {joinCode}
                        </span>
                        <button
                          onClick={() => copyToClipboardText(joinCode)}
                          className="p-1 hover:bg-gray-100 rounded"
                          title="Copy code"
                        >
                          <Copy className="w-4 h-4 text-gray-500" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <button className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold py-3 rounded-xl hover:from-emerald-600 hover:to-teal-700 transition-all duration-300">
                      <Link to={"/messaging"}>Open WhatsApp</Link>
                    </button>
                    {currentStep === 3 && (
                      <button
                        onClick={handleNextStep}
                        className="px-6 bg-gray-100 text-gray-700 font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors"
                      >
                        Complete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {currentStep === 4 && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8 shadow-xl border border-green-200">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-green-900 mb-2">
                    Setup Complete!
                  </h2>
                  <p className="text-green-700">
                    Your WhatsApp integration is now active and ready to use.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - QR Code */}
          <div className="lg:sticky lg:top-24">
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-emerald-100 text-center">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Scan QR Code on Mobile
                </h3>
                <p className="text-gray-500">
                  Quick setup using your mobile device
                </p>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-emerald-50 p-8 rounded-2xl border border-gray-200 mb-6">
                <div className="w-48 h-48 mx-auto bg-white p-4 rounded-xl shadow-sm border">
                  {/* QR Code Placeholder */}
                  <div className="w-full h-full bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-lg flex items-center justify-center">
                    <div className="grid grid-cols-8 gap-1 w-32 h-32">
                      {Array.from({ length: 64 }).map((_, i) => (
                        <div
                          key={i}
                          className={`aspect-square rounded-sm ${
                            Math.random() > 0.5 ? "bg-gray-900" : "bg-white"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                  <span>Twilio WhatsApp Sandbox</span>
                </div>
                <p className="text-xs text-gray-400">
                  Scan this code with your mobile device to quickly connect
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSetup;
